/**
 * 託送実績由来の電力量を四捨五入して 3 桁区切りの文字列にする。
 *
 * 表示値 (送電端) は「欠損補完含む需要実績 ÷ (1 − 損失率)」で算出されるため、
 * 格納値が小数を持つ (例: 58767.393561817)。そのまま出すと画面に小数点が並ぶので
 * 表示時に丸める (2026-07-31 竹ノ下さん指示)。同じ実績を読む画面はすべて対象:
 * 同時同量 (需要 / 発電) と 需要想定 の実績列・実績差分列。
 *
 * 丸めるのは表示だけで、誤差率などの計算は backend が元の精度で行う。需要想定値
 * も保持値は小数のまま、表示だけこの関数で整数にする (AC-DFC-EDIT-003)。
 *
 * `Math.round` は half-up (+∞ 方向) なので負値では「四捨五入」にならない
 * (-1234.5 → -1234)。実績との差分や調達量・販売量は符号を持つため、絶対値で
 * 丸めてから符号を戻す (backend の shopspring/decimal Round(0) と同じ
 * half-away-from-zero)。
 *
 * NOTE: 単位ラベル (kWh / MWh) は呼び出し側が持つ。本関数は丸め方だけを担う。
 *
 * why not チャート系列のプロット値にも使う: 戻り値は表示テキスト専用であり、
 * Recharts の dataKey (系列描画値) には使わない — 描画は小数を保持する
 * (doji-bg.md AC-BDB-029a / conventions.md「チャートの描画値と軸目盛りの
 * 丸め方は分けて扱う」)。
 */
export function formatKwh(value: number): string {
  return roundKwh(value).toLocaleString('ja-JP')
}

/**
 * 託送実績由来の電力量を `formatKwh` と同じ規則で整数へ丸める (数値を返す版)。
 *
 * `formatKwh` が表示テキストを返すのに対し、こちらは丸めた数値そのものを必要と
 * する経路用。表示テキストと異なる整数にならないよう、丸め方を共有する。
 * (参照列の採用値は丸めず保持する〈AC-DFC-EDIT-003〉。)
 *
 * 計算途中の IEEE 754 誤差を吸収する epsilon 補正は入れない。生の託送実績値
 * (例: 979.4999999995) は 0.5 未満として素直に切り捨てる — 補正を入れると表示
 * (979) と食い違う。
 */
export function roundKwh(value: number): number {
  const rounded = Math.sign(value) * Math.round(Math.abs(value))
  // -0 を 0 に正規化する (Math.sign(-0.4) * 0 = -0)。
  return rounded === 0 ? 0 : rounded
}
